import Axios from "axios";
const api = Axios.create({ withCredentials: true });
const createApiFunctions = (baseURL: string, acceptLanguageValue?: string) => {
    api.defaults.baseURL = baseURL;
    if (acceptLanguageValue != null) {
        api.defaults.headers.common["Accept-Language"] = acceptLanguageValue;
    }

    const apiFunctions = {
        AdminOne: {
            MigrateRecipients: Object.assign(
                () => api.get<string>(`/api/v1/adminone/MigrateRecipients`, undefined),
                { absoluteUrl: () => `${baseURL}/api/v1/adminone/MigrateRecipients`, requestSizeLimit: 3145728 }
            ),
            CreateShowToDateRecords: Object.assign(
                (truncateExisting: boolean) => api.post<server.dto.ApiResponseWrapper<string>>(`/api/v1/adminone/createshowtodaterecords`, undefined, { params: { truncateExisting } }),
                { absoluteUrl: (truncateExisting: boolean) => `${baseURL}/api/v1/adminone/createshowtodaterecords?truncateExisting=${truncateExisting}`, requestSizeLimit: 3145728 }
            ),
            RefreshExternalIdentities: Object.assign(
                (modifiedLongerThanHours: number) => api.post<string>(`/api/v1/adminone/updateexternalidentities`, undefined, { params: { modifiedLongerThanHours } }),
                { absoluteUrl: (modifiedLongerThanHours: number) => `${baseURL}/api/v1/adminone/updateexternalidentities?modifiedLongerThanHours=${modifiedLongerThanHours}`, requestSizeLimit: 3145728 }
            )
        },
        Artists: {
            GetArtistsForCurrentUserPaginated: Object.assign(
                (excludeIds: number[], keyword: string, page: number, per_page: number) => api.post<server.dto.ApiResponseWrapper<server.dto.LegacyArtist[]>>(`/api/v1/artists/myartists`, excludeIds, { params: { keyword, page, per_page } }),
                { absoluteUrl: (keyword: string, page: number, per_page: number) => `${baseURL}/api/v1/artists/myartists?keyword=${keyword}&page=${page}&per_page=${per_page}`, requestSizeLimit: 3145728 }
            ),
            ListAppointments: Object.assign(
                (artistId: number, timeMin: Date, timeMax: Date, page: number, per_page: number, appointmentTypes: server.dto.AppointmentTypes[]) => api.get<server.dto.ApiResponseWrapper<any[]>>(`/api/v1/artists/${artistId}/appointments`, { params: { timeMin, timeMax, page, per_page, appointmentTypes } }),
                { absoluteUrl: (artistId: number, timeMin: Date, timeMax: Date, page: number, per_page: number, appointmentTypes: server.dto.AppointmentTypes[]) => `${baseURL}/api/v1/artists/${artistId}/appointments?timeMin=${timeMin}&timeMax=${timeMax}&page=${page}&per_page=${per_page}&appointmentTypes=${appointmentTypes}`, requestSizeLimit: 3145728 }
            )
        },
        Companies: {
            CachePointOfInterestMarkers: Object.assign(
                (companyId: number, markers: server.dto.AddressMarker[]) => api.post<server.dto.ApiResponse>(`/api/v1/companies/${companyId}/markers`, markers, undefined),
                { absoluteUrl: (companyId: number) => `${baseURL}/api/v1/companies/${companyId}/markers`, requestSizeLimit: 3145728 }
            ),
            GetPointOfInterestMarkers: Object.assign(
                (companyId: number) => api.get<server.dto.AddressMarker[]>(`/api/v1/companies/${companyId}/markers`, undefined),
                { absoluteUrl: (companyId: number) => `${baseURL}/api/v1/companies/${companyId}/markers`, requestSizeLimit: 3145728 }
            ),
            GetNearbyAirports: Object.assign(
                (companyId: number, radius: number) => api.get<server.dto.Airport[]>(`/api/v1/companies/${companyId}/nearbyAirports`, { params: { radius } }),
                { absoluteUrl: (companyId: number, radius: number) => `${baseURL}/api/v1/companies/${companyId}/nearbyAirports?radius=${radius}`, requestSizeLimit: 3145728 }
            ),
            GetLegacyDetails: Object.assign(
                (companyId: number) => api.get<server.dto.LegacyCompany>(`/api/v1/companies/${companyId}`, undefined),
                { absoluteUrl: (companyId: number) => `${baseURL}/api/v1/companies/${companyId}`, requestSizeLimit: 3145728 }
            ),
            FilesGet: Object.assign(
                (companyId: number, page: number, per_page: number) => api.get<server.dto.ApiResponseWrapper<server.dto.LegacyFile[]>>(`/api/v1/companies/${companyId}/files`, { params: { page, per_page } }),
                { absoluteUrl: (companyId: number, page: number, per_page: number) => `${baseURL}/api/v1/companies/${companyId}/files?page=${page}&per_page=${per_page}`, requestSizeLimit: 3145728 }
            )
        },
        Contacts: {
            GetDetails: Object.assign(
                (contactId: number) => api.get<server.dto.LegacyContactSmall>(`/api/v1/contacts/${contactId}`, undefined),
                { absoluteUrl: (contactId: number) => `${baseURL}/api/v1/contacts/${contactId}`, requestSizeLimit: 3145728 }
            ),
            FilesGet: Object.assign(
                (contactId: number, page: number, per_page: number) => api.get<server.dto.ApiResponseWrapper<server.dto.LegacyFile[]>>(`/api/v1/contacts/${contactId}/files`, { params: { page, per_page } }),
                { absoluteUrl: (contactId: number, page: number, per_page: number) => `${baseURL}/api/v1/contacts/${contactId}/files?page=${page}&per_page=${per_page}`, requestSizeLimit: 3145728 }
            )
        },
        ExternalCalendars: {
            GetExternalEvent: Object.assign(
                (externalCalendarId: number, externalEventId: number) => api.get<any>(`/api/v1/externalcalendars/${externalCalendarId}/events/${externalEventId}`, undefined),
                { absoluteUrl: (externalCalendarId: number, externalEventId: number) => `${baseURL}/api/v1/externalcalendars/${externalCalendarId}/events/${externalEventId}`, requestSizeLimit: 3145728 }
            )
        },
        ExternalEventsources: {
            GetExternalEvent: Object.assign(
                (externalCalendarId: number, externalEventId: number) => api.get<any>(`/api/v1/externaleventsources/${externalCalendarId}/events/${externalEventId}`, undefined),
                { absoluteUrl: (externalCalendarId: number, externalEventId: number) => `${baseURL}/api/v1/externaleventsources/${externalCalendarId}/events/${externalEventId}`, requestSizeLimit: 3145728 }
            )
        },
        Files: {
            DownloadClientFile: Object.assign(
                (fileId: string, fileName: string) => api.get<any>(`/api/v1/files/clientfile/${fileId}/download/${fileName}`, undefined),
                { absoluteUrl: (fileId: string, fileName: string) => `${baseURL}/api/v1/files/clientfile/${fileId}/download/${fileName}`, requestSizeLimit: 3145728 }
            ),
            DownloadFlightFile: Object.assign(
                (fileId: string, fileName: string) => api.get<any>(`/api/v1/files/flight/${fileId}/download/${fileName}`, undefined),
                { absoluteUrl: (fileId: string, fileName: string) => `${baseURL}/api/v1/files/flight/${fileId}/download/${fileName}`, requestSizeLimit: 3145728 }
            ),
            DownloadTrainFile: Object.assign(
                (fileId: string, fileName: string) => api.get<any>(`/api/v1/files/train/${fileId}/download/${fileName}`, undefined),
                { absoluteUrl: (fileId: string, fileName: string) => `${baseURL}/api/v1/files/train/${fileId}/download/${fileName}`, requestSizeLimit: 3145728 }
            )
        },
        Flights: {
            GetLegacySuperFlight: Object.assign(
                (flightId: number) => api.get<any>(`/api/v1/flights/${flightId}`, undefined),
                { absoluteUrl: (flightId: number) => `${baseURL}/api/v1/flights/${flightId}`, requestSizeLimit: 3145728 }
            ),
            ListFlightFiles: Object.assign(
                (flightId: number) => api.get<server.dto.LegacyFile[]>(`/api/v1/flights/${flightId}/files`, undefined),
                { absoluteUrl: (flightId: number) => `${baseURL}/api/v1/flights/${flightId}/files`, requestSizeLimit: 3145728 }
            )
        },
        GroundTransports: {
            GetLegacyGroundTransport: Object.assign(
                (groundTransportId: number) => api.get<any>(`/api/v1/groundtransports/${groundTransportId}`, undefined),
                { absoluteUrl: (groundTransportId: number) => `${baseURL}/api/v1/groundtransports/${groundTransportId}`, requestSizeLimit: 3145728 }
            )
        },
        Guestlist: {
            GetGuestlistByShow: Object.assign(
                (showId: number) => api.get<server.dto.ApiResponseWrapperWithRequest<server.dto.LegacyGuestlist, Object>>(`/api/v1/guestlist/${showId}`, undefined),
                { absoluteUrl: (showId: number) => `${baseURL}/api/v1/guestlist/${showId}`, requestSizeLimit: 3145728 }
            ),
            UpdateGuestlist: Object.assign(
                (dto: server.dto.LegacyGuestlist) => api.post<server.dto.ApiResponseWrapperWithRequest<string, Object>>(`/api/v1/guestlist`, dto, undefined),
                { absoluteUrl: () => `${baseURL}/api/v1/guestlist`, requestSizeLimit: 3145728 }
            ),
            AddGuestlist: Object.assign(
                (showId: number) => api.put<server.dto.ApiResponseWrapperWithRequest<string, Object>>(`/api/v1/guestlist/${showId}`, undefined, undefined),
                { absoluteUrl: (showId: number) => `${baseURL}/api/v1/guestlist/${showId}`, requestSizeLimit: 3145728 }
            ),
            DeleteGuestlist: Object.assign(
                (guestlistId: number) => api.delete(`/api/v1/guestlist/${guestlistId}`, undefined),
                { absoluteUrl: (guestlistId: number) => `${baseURL}/api/v1/guestlist/${guestlistId}`, requestSizeLimit: 3145728 }
            ),
            AddGuestlistPlace: Object.assign(
                (dto: server.dto.LegacyGuestlistPlace, guestlistId: number) => api.put<server.dto.ApiResponseWrapperWithRequest<string, Object>>(`/api/v1/guestlist/place`, dto, { params: { guestlistId } }),
                { absoluteUrl: (guestlistId: number) => `${baseURL}/api/v1/guestlist/place?guestlistId=${guestlistId}`, requestSizeLimit: 3145728 }
            ),
            LegacyRemoveGuestlistPlace: Object.assign(
                (placeId: number) => api.delete(`/api/v1/guestlist/place`, undefined),
                { absoluteUrl: () => `${baseURL}/api/v1/guestlist/place`, requestSizeLimit: 3145728 }
            ),
            RemoveGuestlistPlace: Object.assign(
                (placeId: number) => api.delete(`/api/v1/guestlist/place/${placeId}`, undefined),
                { absoluteUrl: (placeId: number) => `${baseURL}/api/v1/guestlist/place/${placeId}`, requestSizeLimit: 3145728 }
            ),
            UpdateGuestlistPlace: Object.assign(
                (dto: server.dto.LegacyGuestlistPlace) => api.post<any>(`/api/v1/guestlist/place`, dto, undefined),
                { absoluteUrl: () => `${baseURL}/api/v1/guestlist/place`, requestSizeLimit: 3145728 }
            ),
            AddGuest: Object.assign(
                (dto: server.dto.LegacyGuest, placeId: number) => api.put<server.dto.ApiResponseWrapperWithRequest<string, Object>>(`/api/v1/guestlist/place/${placeId}/guest`, dto, undefined),
                { absoluteUrl: (placeId: number) => `${baseURL}/api/v1/guestlist/place/${placeId}/guest`, requestSizeLimit: 3145728 }
            ),
            LegacyRemoveGuest: Object.assign(
                (guestId: number) => api.delete(`/api/v1/guestlist/place/guest`, undefined),
                { absoluteUrl: () => `${baseURL}/api/v1/guestlist/place/guest`, requestSizeLimit: 3145728 }
            ),
            RemoveGuest: Object.assign(
                (guestId: number) => api.delete(`/api/v1/guestlist/place/guest/${guestId}`, undefined),
                { absoluteUrl: (guestId: number) => `${baseURL}/api/v1/guestlist/place/guest/${guestId}`, requestSizeLimit: 3145728 }
            ),
            UpdateGuest: Object.assign(
                (dto: server.dto.LegacyGuest) => api.post<server.dto.ApiResponseWrapperWithRequest<string, Object>>(`/api/v1/guestlist/place/guest`, dto, undefined),
                { absoluteUrl: () => `${baseURL}/api/v1/guestlist/place/guest`, requestSizeLimit: 3145728 }
            )
        },
        Notifications: {
            GetNotifications: Object.assign(
                (forMobile: boolean, page: number, per_page: number) => api.get<server.dto.ApiResponseWrapper<server.dto.Notification[]>>(`/api/v1/notifications`, { params: { forMobile, page, per_page } }),
                { absoluteUrl: (forMobile: boolean, page: number, per_page: number) => `${baseURL}/api/v1/notifications?forMobile=${forMobile}&page=${page}&per_page=${per_page}`, requestSizeLimit: 3145728 }
            ),
            MarkRead: Object.assign(
                (notifications: number[], read: boolean) => api.post<server.dto.ApiResponse>(`/api/v1/notifications/read/${read}`, notifications, undefined),
                { absoluteUrl: (read: boolean) => `${baseURL}/api/v1/notifications/read/${read}`, requestSizeLimit: 3145728 }
            ),
            MarkAllSeen: Object.assign(
                (forMobile: boolean) => api.post<server.dto.ApiResponse>(`/api/v1/notifications/allseen`, undefined, { params: { forMobile } }),
                { absoluteUrl: (forMobile: boolean) => `${baseURL}/api/v1/notifications/allseen?forMobile=${forMobile}`, requestSizeLimit: 3145728 }
            )
        },
        Promoters: {
            GetDetails: Object.assign(
                (promoterId: number) => api.get<server.dto.LegacyPromoter>(`/api/v1/promoters/${promoterId}`, undefined),
                { absoluteUrl: (promoterId: number) => `${baseURL}/api/v1/promoters/${promoterId}`, requestSizeLimit: 3145728 }
            )
        },
        Register: {
            DeleteByPns: Object.assign(
                (pns: any) => api.delete(`/api/v1/register/pns`, undefined),
                { absoluteUrl: () => `${baseURL}/api/v1/register/pns`, requestSizeLimit: 3145728 }
            )
        },
        Shows: {
            GetLegacyShow: Object.assign(
                (showId: number) => api.get<any>(`/api/v1/shows/${showId}`, undefined),
                { absoluteUrl: (showId: number) => `${baseURL}/api/v1/shows/${showId}`, requestSizeLimit: 3145728 }
            ),
            FilesGet: Object.assign(
                (showId: number, page: number, per_page: number) => api.get<server.dto.ApiResponseWrapper<server.dto.LegacyFile[]>>(`/api/v1/shows/${showId}/files`, { params: { page, per_page } }),
                { absoluteUrl: (showId: number, page: number, per_page: number) => `${baseURL}/api/v1/shows/${showId}/files?page=${page}&per_page=${per_page}`, requestSizeLimit: 3145728 }
            )
        },
        Tests: {
            HelloWorld: Object.assign(
                () => api.get<string>(`/api/v1/tests/hi`, undefined),
                { absoluteUrl: () => `${baseURL}/api/v1/tests/hi`, requestSizeLimit: 3145728 }
            ),
            TestException: Object.assign(
                () => api.get<string>(`/api/v1/tests/exception/sync`, undefined),
                { absoluteUrl: () => `${baseURL}/api/v1/tests/exception/sync`, requestSizeLimit: 3145728 }
            ),
            TestExceptionAsync: Object.assign(
                () => api.get<server.dto.ApiResponse>(`/api/v1/tests/exception/async`, undefined),
                { absoluteUrl: () => `${baseURL}/api/v1/tests/exception/async`, requestSizeLimit: 3145728 }
            ),
            TestExceptionInMediator: Object.assign(
                () => api.get<string>(`/api/v1/tests/exception/mediator`, undefined),
                { absoluteUrl: () => `${baseURL}/api/v1/tests/exception/mediator`, requestSizeLimit: 3145728 }
            )
        },
        Trains: {
            GetTrain: Object.assign(
                (trainId: number) => api.get<server.dto.LegacyTrain>(`/api/v1/trains/${trainId}`, undefined),
                { absoluteUrl: (trainId: number) => `${baseURL}/api/v1/trains/${trainId}`, requestSizeLimit: 3145728 }
            ),
            CreateTrain: Object.assign(
                (dto: server.dto.CreateTrainInstruction) => api.post<server.dto.LegacyTrain>(`/api/v1/trains/create`, dto, undefined),
                { absoluteUrl: () => `${baseURL}/api/v1/trains/create`, requestSizeLimit: 3145728 }
            ),
            UpdateTrainDetails: Object.assign(
                (trainId: number, dto: server.dto.ModifyTrainDetailsInstruction) => api.put<server.dto.LegacyTrain>(`/api/v1/trains/${trainId}/update`, dto, undefined),
                { absoluteUrl: (trainId: number) => `${baseURL}/api/v1/trains/${trainId}/update`, requestSizeLimit: 3145728 }
            ),
            Delete: Object.assign(
                (trainId: number) => api.delete(`/api/v1/trains/${trainId}`, undefined),
                { absoluteUrl: (trainId: number) => `${baseURL}/api/v1/trains/${trainId}`, requestSizeLimit: 3145728 }
            ),
            CopyTrain: Object.assign(
                (trainId: number) => api.put<number>(`/api/v1/trains/${trainId}/copy`, undefined, undefined),
                { absoluteUrl: (trainId: number) => `${baseURL}/api/v1/trains/${trainId}/copy`, requestSizeLimit: 3145728 }
            ),
            ListTrainFiles: Object.assign(
                (trainId: number) => api.get<server.dto.LegacyFile[]>(`/api/v1/trains/${trainId}/files`, undefined),
                { absoluteUrl: (trainId: number) => `${baseURL}/api/v1/trains/${trainId}/files`, requestSizeLimit: 3145728 }
            ),
            GetTrainBySegment: Object.assign(
                (trainSegmentId: number) => api.get<server.dto.LegacyTrain>(`/api/v1/trains/segments/${trainSegmentId}`, undefined),
                { absoluteUrl: (trainSegmentId: number) => `${baseURL}/api/v1/trains/segments/${trainSegmentId}`, requestSizeLimit: 3145728 }
            ),
            CreateSegment: Object.assign(
                (trainId: number, dto: server.dto.ModifyTrainSegmentInstruction) => api.put<server.dto.LegacyTrain>(`/api/v1/trains/${trainId}/segments/create`, dto, undefined),
                { absoluteUrl: (trainId: number) => `${baseURL}/api/v1/trains/${trainId}/segments/create`, requestSizeLimit: 3145728 }
            ),
            UpdateSegment: Object.assign(
                (trainSegmentId: number, dto: server.dto.ModifyTrainSegmentInstruction) => api.post<server.dto.LegacyTrain>(`/api/v1/trains/segments/${trainSegmentId}/update`, dto, undefined),
                { absoluteUrl: (trainSegmentId: number) => `${baseURL}/api/v1/trains/segments/${trainSegmentId}/update`, requestSizeLimit: 3145728 }
            ),
            DeleteSegment: Object.assign(
                (trainSegmentId: number) => api.delete(`/api/v1/trains/segments/${trainSegmentId}`, undefined),
                { absoluteUrl: (trainSegmentId: number) => `${baseURL}/api/v1/trains/segments/${trainSegmentId}`, requestSizeLimit: 3145728 }
            ),
            GetTrainSegment: Object.assign(
                (trainId: number, trainSegmentId: number) => api.get<any>(`/api/v1/trains/${trainId}/segments/${trainSegmentId}`, undefined),
                { absoluteUrl: (trainId: number, trainSegmentId: number) => `${baseURL}/api/v1/trains/${trainId}/segments/${trainSegmentId}`, requestSizeLimit: 3145728 }
            )
        },
        Users: {
            GetCurrentUser: Object.assign(
                () => api.get<any>(`/api/v1/users/user`, undefined),
                { absoluteUrl: () => `${baseURL}/api/v1/users/user`, requestSizeLimit: 3145728 }
            ),
            ConnectSocial: Object.assign(
                (issuer: string, token: string) => api.get<any>(`/api/v1/users/user/sociallogin`, { params: { issuer, token } }),
                { absoluteUrl: (issuer: string, token: string) => `${baseURL}/api/v1/users/user/sociallogin?issuer=${issuer}&token=${token}`, requestSizeLimit: 3145728 }
            ),
            UpdateAvatar: Object.assign(
                (userId: number) => api.post<server.dto.FileUploadResult>(`/api/v1/users/${userId}/avatar`, undefined, undefined),
                { absoluteUrl: (userId: number) => `${baseURL}/api/v1/users/${userId}/avatar`, requestSizeLimit: 3145728 }
            )
        },
        Venues: {
            GetDetails: Object.assign(
                (venueId: number) => api.get<any>(`/api/v1/venues/${venueId}`, undefined),
                { absoluteUrl: (venueId: number) => `${baseURL}/api/v1/venues/${venueId}`, requestSizeLimit: 3145728 }
            )
        },
    };
    (window as any).Api = apiFunctions;
    return apiFunctions;
}
export { createApiFunctions, api };